/* @jsx h */
import { defineComponent, h } from "vue";
import {
  DEFAULT_RESERVATION_SETTINGS,
  type ReservationSettingsUpdateHandler,
  RestaurantSettingKey,
} from "@lemmonhq/types";
import { useLocalization } from "@lemmonhq/uikit";

export interface PaymentDepositsProps {
  depositEnabled: boolean;
  depositAmount: number;
  cancellationDeadlineHours: number;
  onUpdate: ReservationSettingsUpdateHandler;
}

export const PaymentDeposits = defineComponent<PaymentDepositsProps>({
  name: "PaymentDeposits",
  props: [
    "depositEnabled",
    "depositAmount",
    "cancellationDeadlineHours",
    "onUpdate",
  ],
  setup(props) {
    const { t } = useLocalization();

    return () => (
      <div class="space-y-6">
        {/* Enable Deposits */}
        <div class="form-control">
          <label class="label cursor-pointer justify-start gap-4">
            <input
              type="checkbox"
              class="toggle toggle-primary"
              checked={props.depositEnabled}
              onChange={(e) =>
                props.onUpdate(
                  "reservations_require_deposit",
                  (e.target as HTMLInputElement).checked,
                )
              }
            />
            <div>
              <span class="label-text  font-medium">
                {t("settings.reservations.require_deposit")}
              </span>
              <p class="text-sm  mt-1">
                {t("settings.reservations.require_deposit_desc")}
              </p>
            </div>
          </label>
        </div>

        {props.depositEnabled && (
          <div class="space-y-5 pl-1 border-l-2 border-primary/20 ml-2 pl-4">
            {/* Info about per-slot configuration */}
            <div class="bg-info/10 border border-info/20 rounded-lg p-3">
              <div class="flex items-start gap-2">
                <span class="material-symbols-outlined text-info text-lg mt-0.5">
                  info
                </span>
                <div class="text-xs text-base-content/70">
                  <p>
                    Deposit amounts are configured per time slot in the{" "}
                    <strong>Service Periods</strong> section above (the
                    "Pre-payment" field on each period). When a customer books a
                    time slot with a pre-payment amount, their card will be
                    pre-authorized for that amount.
                  </p>
                  <p class="mt-2">
                    The hold is released when the reservation is completed. If
                    the customer doesn't show up or cancels after the deadline,
                    the deposit is charged.
                  </p>
                </div>
              </div>
            </div>

            {/* Cancellation Deadline */}
            <div>
              <label class="text-sm font-medium text-base-content">
                Free cancellation deadline (hours before reservation)
              </label>
              <p class="text-xs text-base-content/50 mb-2">
                Customers can cancel for free up to this many hours before the
                reservation time. After this deadline, the deposit will be
                charged.
              </p>
              <input
                type="number"
                class="input input-bordered input-sm w-32"
                min={0}
                max={168}
                value={props.cancellationDeadlineHours}
                onInput={(e: any) =>
                  props.onUpdate(
                    "reservations_cancellation_deadline_hours",
                    parseInt(e.target.value) || 24,
                  )
                }
              />
              <span class="text-xs text-base-content/50 ml-2">hours</span>
            </div>

            {/* Deposit flow explanation */}
            <div class="bg-base-200 rounded-lg p-4 text-xs text-base-content/60 space-y-2">
              <p class="font-medium text-base-content/80">How deposits work:</p>
              <ol class="list-decimal list-inside space-y-1">
                <li>Customer books a time slot with a pre-payment amount</li>
                <li>
                  Customer's card is <strong>pre-authorized</strong> (held, not
                  charged)
                </li>
                <li>
                  If cancelled before the deadline: hold is released (no charge)
                </li>
                <li>
                  If cancelled after the deadline: deposit is charged + customer
                  is notified
                </li>
                <li>If no-show: deposit is charged + customer is notified</li>
                <li>
                  If reservation is completed: hold is released automatically
                </li>
              </ol>
            </div>
          </div>
        )}
      </div>
    );
  },
});
